import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Autocomplete, Dialog, InputAdornment, MenuItem, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import PersonOffRoundedIcon from "@mui/icons-material/PersonOffRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import BlockIcon from "@mui/icons-material/Block";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { selectUser } from "../../../features/userSlice";
import { branches as BUILT_IN_BRANCHES } from "../Utilities/api";
import { requestSessionRefresh } from "../../../utils/liveSession";
import { EmptyState, LoadingState, StatTile } from "../../../components/UI/States";
import {
  bulkUpdateAccessUsers,
  createCustomRole,
  fetchAccessOverview,
  fetchAccessUsers,
} from "./accessApi";
import { buildPageCatalog, buildRoleCatalog } from "./accessCatalog";
import { Notice, RoleChip, UserAvatar, isOnline, timeAgo } from "./accessUi";
import UserDrawer from "./UserDrawer";

const PAGE_SIZE = 30;
const ROLES_SHOWN = 4;
const NO_ROLES = "__none__";

// Quick filters from the stat tiles.
const VIEWS = {
  active: { label: "Active", test: (u) => !u.deactivated },
  online: { label: "Online now", test: (u) => !u.deactivated && isOnline(u) },
  noRoles: { label: "No roles", test: (u) => !u.deactivated && !(u.roles || []).length },
  override: { label: "Branch set here", test: (u) => !u.deactivated && Boolean(u.branch_override) },
  deactivated: { label: "Deactivated", test: (u) => u.deactivated },
};

const SORTS = {
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  branch: (a, b) => {
    if (!a.branch && !b.branch) return 0;
    if (!a.branch) return 1;
    if (!b.branch) return -1;
    return a.branch.localeCompare(b.branch);
  },
  roles: (a, b) => (a.roles?.length || 0) - (b.roles?.length || 0),
  seen: (a, b) =>
    (a.last_seen ? new Date(a.last_seen).getTime() : 0) - (b.last_seen ? new Date(b.last_seen).getTime() : 0),
};

const userKey = (u) => u.id || u._id;

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const BranchCell = ({ user }) =>
  user.branch ? (
    <span className="inline-flex items-center gap-1.5">
      {user.branch}
      {user.branch_override && (
        <span
          title={`Set in the User Manager${user.directory_branch ? ` — directory says ${user.directory_branch}` : ""}`}
          className="rounded-full bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300"
        >
          Set here
        </span>
      )}
    </span>
  ) : (
    <span className="app-muted text-xs">—</span>
  );

const RolesCell = ({ roles, customRoles }) =>
  roles?.length ? (
    <div className="flex max-w-md flex-wrap gap-1">
      {roles.slice(0, ROLES_SHOWN).map((r) => (
        <RoleChip key={r} role={r} tone={customRoles.has(r) ? "custom" : "extra"} />
      ))}
      {roles.length > ROLES_SHOWN && (
        <span className="app-muted self-center text-xs">+{roles.length - ROLES_SHOWN}</span>
      )}
    </div>
  ) : (
    <span className="app-muted text-xs">No roles</span>
  );

// The User Manager list: find people, filter by branch/role/status, edit one
// in the side panel or several at once with the bulk bar.
const UserDirectory = () => {
  const actor = useSelector(selectUser);
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [query, setQuery] = useState("");
  const [view, setView] = useState("active");
  const [branchFilter, setBranchFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sort, setSort] = useState({ key: "name", desc: false });
  const [page, setPage] = useState(0);

  const [selected, setSelected] = useState(() => new Set());
  const [openId, setOpenId] = useState(null);
  const [bulk, setBulk] = useState(null); // "addRole" | "removeRole" | "branch"

  const pages = useMemo(() => buildPageCatalog(), []);

  const load = useCallback(async () => {
    if (!actor?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [list, nextOverview] = await Promise.all([
        fetchAccessUsers(actor.id),
        fetchAccessOverview(actor.id),
      ]);
      setUsers(list.users || []);
      setOverview(nextOverview);
    } catch (err) {
      setError(err.message || "Unable to load users right now.");
    } finally {
      setLoading(false);
    }
  }, [actor?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const roleCatalog = useMemo(
    () => buildRoleCatalog({ customRoles: overview?.customRoles, users, pages }),
    [overview, users, pages]
  );
  const customRoles = useMemo(
    () => new Set(roleCatalog.filter((r) => r.custom).map((r) => r.name)),
    [roleCatalog]
  );
  const grantsByKey = useMemo(
    () => new Map((overview?.grants || []).map((g) => [g.key, g])),
    [overview]
  );
  const branchOptions = useMemo(
    () =>
      [
        ...new Set([
          ...BUILT_IN_BRANCHES,
          ...users.flatMap((u) => [u.branch, u.directory_branch, u.branch_override]),
        ]),
      ]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [users]
  );

  const counts = useMemo(
    () => Object.fromEntries(Object.entries(VIEWS).map(([key, v]) => [key, users.filter(v.test).length])),
    [users]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = users.filter((u) => {
      if (!VIEWS[view].test(u)) return false;
      if (branchFilter && u.branch !== branchFilter) return false;
      if (roleFilter === NO_ROLES && (u.roles || []).length) return false;
      if (roleFilter && roleFilter !== NO_ROLES && !(u.roles || []).includes(roleFilter)) return false;
      if (!q) return true;
      return [u.name, u.email, u.title, u.branch, ...(u.roles || [])]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(q));
    });
    const compare = SORTS[sort.key];
    rows.sort((a, b) => (sort.desc ? -compare(a, b) : compare(a, b)) || SORTS.name(a, b));
    return rows;
  }, [users, query, view, branchFilter, roleFilter, sort]);

  // Back to the first page whenever the filter changes.
  useEffect(() => {
    setPage(0);
  }, [query, view, branchFilter, roleFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const replaceUsers = useCallback((updated) => {
    const byKey = new Map(updated.map((u) => [String(u._id), u]));
    setUsers((prev) => prev.map((u) => byKey.get(String(u._id)) || u));
  }, []);

  const handleRoleCreated = useCallback((role) => {
    setOverview((prev) => ({
      ...prev,
      customRoles: [...(prev?.customRoles || []), role],
    }));
  }, []);

  const handleGrantChange = useCallback((grant) => {
    setOverview((prev) => {
      const rest = (prev?.grants || []).filter((g) => g.key !== grant.key);
      const empty = !grant.roles.length && !grant.users.length;
      return { ...prev, grants: empty ? rest : [...rest, grant] };
    });
  }, []);

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, desc: !prev.desc } : { key, desc: key === "seen" }));

  const toggleSelected = (u) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(u._id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((u) => selected.has(String(u._id)));
  const togglePageSelected = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      pageRows.forEach((u) => (allOnPageSelected ? next.delete(String(u._id)) : next.add(String(u._id))));
      return next;
    });
  const selectAllFiltered = () => setSelected(new Set(filtered.map((u) => String(u._id))));

  const selectedUsers = users.filter((u) => selected.has(String(u._id)));

  const exportCsv = () => {
    const header = ["Name", "Email", "Title", "Branch", "Branch set here", "Roles", "Last seen", "Status"];
    const lines = filtered.map((u) =>
      [
        u.name,
        u.email,
        u.title,
        u.branch,
        u.branch_override ? "Yes" : "",
        (u.roles || []).join("; "),
        u.last_seen ? new Date(u.last_seen).toISOString() : "",
        u.deactivated ? "Deactivated" : "Active",
      ]
        .map(csvCell)
        .join(",")
    );
    const blob = new Blob([[header.map(csvCell).join(","), ...lines].join("\r\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `node-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openUser = users.find((u) => String(u._id) === openId);

  const SortHeader = ({ id, children, className = "" }) => (
    <th className={`!px-4 uppercase tracking-wider ${className}`} aria-sort={sort.key === id ? (sort.desc ? "descending" : "ascending") : "none"}>
      <button type="button" onClick={() => toggleSort(id)} className="inline-flex items-center gap-1 uppercase tracking-wider">
        {children}
        {sort.key === id &&
          (sort.desc ? <ArrowDownwardIcon sx={{ fontSize: 14 }} /> : <ArrowUpwardIcon sx={{ fontSize: 14 }} />)}
      </button>
    </th>
  );

  if (loading && users.length === 0) return <LoadingState label="Loading users…" />;

  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile icon={GroupRoundedIcon} label="Active users" value={counts.active} active={view === "active"} onClick={() => setView("active")} />
        <StatTile icon={CircleRoundedIcon} label="Online now" value={counts.online} tone="emerald" active={view === "online"} onClick={() => setView("online")} />
        <StatTile icon={PersonOffRoundedIcon} label="No roles" value={counts.noRoles} tone="amber" active={view === "noRoles"} onClick={() => setView("noRoles")} />
        <StatTile icon={PlaceRoundedIcon} label="Branch set here" value={counts.override} tone="sky" active={view === "override"} onClick={() => setView("override")} />
        <StatTile icon={BlockIcon} label="Deactivated" value={counts.deactivated} tone="rose" active={view === "deactivated"} onClick={() => setView("deactivated")} />
      </div>

      {error && <Notice message={{ type: "error", text: error }} className="mb-4" />}
      <Notice message={message} className="mb-4" />

      <div className="app-card overflow-hidden rounded-2xl border">
        {/* Toolbar */}
        <div className="app-divider flex flex-col gap-3 border-b px-4 py-3 sm:px-5 lg:flex-row lg:items-center">
          <TextField
            size="small"
            placeholder="Search name, email, title, role…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-0 lg:w-72"
            inputProps={{ "aria-label": "Search users" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <div className="grid grid-cols-2 gap-2 lg:flex">
            <TextField
              select
              size="small"
              label="Branch"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="lg:w-44"
            >
              <MenuItem value="">All branches</MenuItem>
              {branchOptions.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="lg:w-48"
            >
              <MenuItem value="">All roles</MenuItem>
              <MenuItem value={NO_ROLES}>No roles</MenuItem>
              {roleCatalog.map((r) => (
                <MenuItem key={r.name} value={r.name}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="flex gap-2 lg:ml-auto">
            <button type="button" onClick={exportCsv} disabled={!filtered.length} className="app-btn app-btn-secondary">
              <FileDownloadRoundedIcon sx={{ fontSize: 18 }} />
              Export
            </button>
            <button type="button" onClick={load} disabled={loading} className="app-btn app-btn-secondary">
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
          </div>
        </div>

        <div className="app-divider flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2 text-sm sm:px-5">
          <p className="app-muted tabular-nums">
            <span className="font-semibold text-[color:var(--app-text)]">{filtered.length}</span>{" "}
            {VIEWS[view].label.toLowerCase()} {filtered.length === 1 ? "user" : "users"}
            {(query || branchFilter || roleFilter) && " matching"}
          </p>
          {(query || branchFilter || roleFilter) && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setBranchFilter("");
                setRoleFilter("");
              }}
              className="app-link text-xs"
            >
              Clear filters
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            compact
            icon={PersonSearchIcon}
            title="No users match"
            description="Try a different search, branch, or role — or another tile above."
          />
        ) : (
          <>
            {/* Phones: cards */}
            <ul className="divide-y divide-[color:var(--app-divider)] md:hidden">
              {pageRows.map((u) => (
                <li key={String(u._id)} className="flex items-start gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(String(u._id))}
                    onChange={() => toggleSelected(u)}
                    aria-label={`Select ${u.name}`}
                    className="mt-3 h-4 w-4 shrink-0 accent-sky-600"
                  />
                  <button type="button" onClick={() => setOpenId(String(u._id))} className="flex min-w-0 flex-1 items-start gap-3 text-left">
                    <span className="relative">
                      <UserAvatar user={u} />
                      {isOnline(u) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-[color:var(--app-surface-bg)]" />}
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate font-medium">{u.name || u.email}</p>
                      <p className="app-muted truncate text-xs">
                        {[u.title, u.branch].filter(Boolean).join(" · ") || "—"}
                      </p>
                      <RolesCell roles={u.roles} customRoles={customRoles} />
                    </div>
                    <ChevronRightRoundedIcon className="app-muted mt-2 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>

            {/* Tablet and up: table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="app-table">
                <thead>
                  <tr>
                    <th className="!w-10 !px-4">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={togglePageSelected}
                        aria-label="Select everyone on this page"
                        className="h-4 w-4 accent-sky-600"
                      />
                    </th>
                    <SortHeader id="name">User</SortHeader>
                    <SortHeader id="branch">Branch</SortHeader>
                    <SortHeader id="roles">Roles</SortHeader>
                    <SortHeader id="seen">Last seen</SortHeader>
                    <th className="!px-4" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((u) => (
                    <tr
                      key={String(u._id)}
                      onClick={() => setOpenId(String(u._id))}
                      className={`cursor-pointer ${u.deactivated ? "opacity-60" : ""}`}
                    >
                      <td className="!px-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(String(u._id))}
                          onChange={() => toggleSelected(u)}
                          aria-label={`Select ${u.name}`}
                          className="h-4 w-4 accent-sky-600"
                        />
                      </td>
                      <td className="!px-4">
                        <div className="flex items-center gap-3">
                          <span className="relative">
                            <UserAvatar user={u} />
                            {isOnline(u) && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-[color:var(--app-surface-bg)]" />}
                          </span>
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 font-medium">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenId(String(u._id));
                                }}
                                className="truncate text-left hover:underline"
                              >
                                {u.name || u.email}
                              </button>
                              {u.deactivated && (
                                <span className="shrink-0 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
                                  Deactivated
                                </span>
                              )}
                            </p>
                            <p className="app-muted truncate text-xs">{u.title || u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="!px-4 whitespace-nowrap">
                        <BranchCell user={u} />
                      </td>
                      <td className="!px-4">
                        <RolesCell roles={u.roles} customRoles={customRoles} />
                      </td>
                      <td className="!px-4 whitespace-nowrap">
                        <p className={isOnline(u) ? "font-medium text-emerald-700 dark:text-emerald-300" : ""}>
                          {isOnline(u) ? "Online now" : timeAgo(u.last_seen)}
                        </p>
                        {u.last_route && <p className="app-muted max-w-[12rem] truncate text-xs">{u.last_route}</p>}
                      </td>
                      <td className="!px-4 text-right">
                        <ChevronRightRoundedIcon className="app-muted" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pageCount > 1 && (
              <div className="app-divider flex items-center justify-between gap-2 border-t px-4 py-3 sm:px-5">
                <span className="app-muted text-sm tabular-nums">
                  Page <strong>{safePage + 1}</strong> of {pageCount}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setPage(safePage - 1)} disabled={safePage === 0} className="app-btn app-btn-sm app-btn-ghost">
                    Prev
                  </button>
                  <button type="button" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount - 1} className="app-btn app-btn-sm app-btn-ghost">
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div
          data-bottom-bar="user-bulk"
          className="app-glass app-glass-rim fixed inset-x-2 bottom-2 z-30 mx-auto flex max-w-3xl flex-wrap items-center gap-2 rounded-2xl border px-4 py-3 sm:inset-x-4"
        >
          <p className="mr-auto text-sm">
            <strong className="tabular-nums">{selected.size}</strong> selected
            {selected.size < filtered.length && (
              <button type="button" onClick={selectAllFiltered} className="app-link ml-2 text-xs">
                Select all {filtered.length}
              </button>
            )}
          </p>
          <button type="button" onClick={() => setBulk("addRole")} className="app-btn app-btn-sm app-btn-secondary">
            Add role
          </button>
          <button type="button" onClick={() => setBulk("removeRole")} className="app-btn app-btn-sm app-btn-secondary">
            Remove role
          </button>
          <button type="button" onClick={() => setBulk("branch")} className="app-btn app-btn-sm app-btn-secondary">
            Set branch
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="app-btn app-btn-sm app-btn-ghost">
            Clear
          </button>
        </div>
      )}
      {/* Room for the bulk bar at the end of the page. */}
      {selected.size > 0 && <div className="h-20" />}

      {bulk && (
        <BulkDialog
          mode={bulk}
          users={selectedUsers}
          roleCatalog={roleCatalog}
          branchOptions={branchOptions}
          actorId={actor?.id}
          onRoleCreated={handleRoleCreated}
          onClose={() => setBulk(null)}
          onDone={(updated, skipped) => {
            replaceUsers(updated);
            setBulk(null);
            setSelected(new Set());
            setMessage({
              type: skipped.length ? "info" : "success",
              text:
                `Updated ${updated.length} ${updated.length === 1 ? "user" : "users"}.` +
                (skipped.length ? ` Skipped ${skipped.length}: ${skipped[0].reason}` : ""),
            });
            if (updated.some((u) => u.id && u.id === actor?.id)) requestSessionRefresh();
          }}
        />
      )}

      {openUser && (
        <UserDrawer
          key={String(openUser._id)}
          user={openUser}
          roleCatalog={roleCatalog}
          branchOptions={branchOptions}
          pages={pages}
          grantsByKey={grantsByKey}
          actorId={actor?.id}
          onUserUpdated={(next) => replaceUsers([next])}
          onGrantChange={handleGrantChange}
          onRoleCreated={handleRoleCreated}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
};

const DIRECTORY = "__directory__";

// Add/remove one role or set one branch on everyone selected.
const BulkDialog = ({ mode, users, roleCatalog, branchOptions, actorId, onRoleCreated, onClose, onDone }) => {
  const [role, setRole] = useState(null);
  const [branch, setBranch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const roleOptions = useMemo(() => {
    if (mode === "removeRole") {
      return [...new Set(users.flatMap((u) => u.roles || []))].sort((a, b) => a.localeCompare(b));
    }
    return roleCatalog.map((r) => r.name);
  }, [mode, users, roleCatalog]);

  const affected =
    mode === "addRole"
      ? users.filter((u) => role && !(u.roles || []).includes(role)).length
      : mode === "removeRole"
      ? users.filter((u) => role && (u.roles || []).includes(role)).length
      : users.length;

  const title =
    mode === "addRole" ? "Add a role" : mode === "removeRole" ? "Remove a role" : "Set branch";
  const ready = mode === "branch" ? Boolean(branch) : Boolean(role) && affected > 0;

  // "Add role" can also create one: a typed name that isn't a role yet.
  const isNewRole =
    mode === "addRole" &&
    Boolean(role) &&
    !roleCatalog.some((r) => r.name.toLowerCase() === role.toLowerCase());

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNewRole) {
        const { role: created } = await createCustomRole(actorId, role, "");
        onRoleCreated(created);
      }
      const change =
        mode === "addRole"
          ? { addRoles: [role] }
          : mode === "removeRole"
          ? { removeRoles: [role] }
          : { branch: branch === DIRECTORY ? null : branch };
      const res = await bulkUpdateAccessUsers(
        actorId,
        users.map(userKey),
        change
      );
      onDone(res.users || [], res.skipped || []);
    } catch (err) {
      setError(err.message || "Couldn't update those users.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }}>
      <div className="space-y-4 p-5">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="app-muted text-sm">
            For {users.length} selected {users.length === 1 ? "user" : "users"}. Each person gets one notification.
          </p>
        </div>
        {mode === "branch" ? (
          <TextField select fullWidth size="small" label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)}>
            <MenuItem value={DIRECTORY}>From the directory (clear what was set here)</MenuItem>
            {branchOptions.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Autocomplete
            size="small"
            options={roleOptions}
            value={role}
            onChange={(e, value) => setRole(value ? String(value).trim().replace(/\s+/g, " ") : null)}
            freeSolo={mode === "addRole"}
            autoSelect={mode === "addRole"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                autoFocus
                helperText={mode === "addRole" ? "Type a new name to create a custom role." : undefined}
              />
            )}
          />
        )}
        {isNewRole && (
          <p className="text-sm text-violet-700 dark:text-violet-300">
            “{role}” isn't a role yet — Apply creates it as a custom role first.
          </p>
        )}
        {mode !== "branch" && role && (
          <p className="app-muted text-sm">
            {affected === 0
              ? mode === "addRole"
                ? "Everyone selected already has it."
                : "Nobody selected has it."
              : `Changes ${affected} ${affected === 1 ? "person" : "people"}.`}
          </p>
        )}
        {error && <Notice message={{ type: "error", text: error }} />}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="app-btn app-btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={busy || !ready}
            className={`app-btn ${mode === "removeRole" ? "app-btn-danger" : "app-btn-primary"}`}
          >
            {busy ? "Saving…" : "Apply"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default UserDirectory;
